import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Typography,
  Modal,
  Form,
  Switch,
  message,
  Tooltip,
  Row,
  Col,
  Statistic,
  Popconfirm
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  BookOutlined,
  ShoppingOutlined,
  DollarOutlined,
  BankOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import journalService, {
  type Journal,
  type JournalType,
  type JournalStats,
  type CreateJournalInput,
  type UpdateJournalInput
} from '@/services/journalService';
import accountService from '@/services/accountService';
import { useAuthStore } from '@/stores/authStore';

const { Title, Text } = Typography;
const { Option } = Select;
const { Search } = Input;

interface Account {
  id: string;
  code: string;
  name: string;
  accountType: string;
}

const journalTypeLabels: Record<JournalType, string> = {
  SALE: 'Sales',
  PURCHASE: 'Purchases',
  CASH: 'Cash',
  BANK: 'Bank',
  GENERAL: 'General'
};

const journalTypeColors: Record<JournalType, string> = {
  SALE: 'green',
  PURCHASE: 'orange',
  CASH: 'blue',
  BANK: 'purple',
  GENERAL: 'default'
};

const journalTypeIcons: Record<JournalType, React.ReactNode> = {
  SALE: <FileTextOutlined />,
  PURCHASE: <ShoppingOutlined />,
  CASH: <DollarOutlined />,
  BANK: <BankOutlined />,
  GENERAL: <BookOutlined />
};

const JournalManagement: React.FC = () => {
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [journals, setJournals] = useState<Journal[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [stats, setStats] = useState<JournalStats | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingJournal, setEditingJournal] = useState<Journal | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<JournalType | 'all'>('all');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch journals and accounts first (these are essential)
      const [journalsData, accountsData] = await Promise.all([
        journalService.getJournals(),
        accountService.getAccounts()
      ]);
      setJournals(journalsData || []);
      setAccounts(accountsData || []);

      // Fetch stats separately (non-essential, can fail gracefully)
      try {
        const statsData = await journalService.getJournalStats();
        setStats(statsData);
      } catch (statsError) {
        console.warn('Failed to fetch journal stats:', statsError);
        // Set default stats if the endpoint fails
        setStats({
          total: journalsData?.length || 0,
          active: journalsData?.filter((j: Journal) => j.active).length || 0,
          inactive: journalsData?.filter((j: Journal) => !j.active).length || 0,
          byType: []
        });
      }
    } catch (error: any) {
      console.error('Failed to fetch data:', error);
      message.error(error.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingJournal(null);
    form.resetFields();
    form.setFieldsValue({ active: true });
    setModalVisible(true);
  };

  const handleEdit = (journal: Journal) => {
    setEditingJournal(journal);
    form.setFieldsValue({
      code: journal.code,
      name: journal.name,
      type: journal.type,
      defaultAccountId: journal.defaultAccountId,
      active: journal.active
    });
    setModalVisible(true);
  };

  const handleDelete = async (journal: Journal) => {
    try {
      await journalService.deleteJournal(journal.id);
      message.success('Journal deleted successfully');
      fetchData();
    } catch (error: any) {
      message.error(error.message || 'Failed to delete journal. It may have associated entries.');
    }
  };

  const handleToggleActive = async (journal: Journal) => {
    try {
      if (journal.active) {
        await journalService.updateJournal(journal.id, { active: false });
        message.success('Journal deactivated');
      } else {
        await journalService.updateJournal(journal.id, { active: true });
        message.success('Journal activated');
      }
      fetchData();
    } catch (error: any) {
      message.error(error.message || 'Failed to update journal status');
    }
  };

  const handleSubmit = async (values: any) => {
    if (!user?.companyId) {
      message.error('Company ID not found. Please log in again.');
      return;
    }

    setLoading(true);
    try {
      // Handle type field - tags mode returns array, we need string
      const journalType = Array.isArray(values.type) ? values.type[0] : values.type;

      if (editingJournal) {
        const updateData: UpdateJournalInput = {
          name: values.name,
          code: values.code,
          type: journalType,
          defaultAccountId: values.defaultAccountId || null,
          active: values.active
        };
        await journalService.updateJournal(editingJournal.id, updateData);
        message.success('Journal updated successfully');
      } else {
        const createData: CreateJournalInput = {
          name: values.name,
          code: values.code,
          type: journalType,
          defaultAccountId: values.defaultAccountId || undefined,
          companyId: user.companyId
        };
        await journalService.createJournal(createData);
        message.success('Journal created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingJournal(null);
      fetchData();
    } catch (error: any) {
      message.error(error.message || 'Failed to save journal');
    } finally {
      setLoading(false);
    }
  };

  // Filter journals
  const filteredJournals = journals.filter(journal => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesCode = journal.code.toLowerCase().includes(query);
      const matchesName = journal.name.toLowerCase().includes(query);
      if (!matchesCode && !matchesName) return false;
    }

    // Type filter
    if (filterType !== 'all' && journal.type !== filterType) {
      return false;
    }

    // Active filter
    if (filterActive === 'active' && !journal.active) return false;
    if (filterActive === 'inactive' && journal.active) return false;

    return true;
  });

  // Get default account info for a journal type
  const getDefaultAccountForType = (type: JournalType): string | undefined => {
    switch (type) {
      case 'SALE':
        return accounts.find(a => a.code === '4000')?.id;
      case 'PURCHASE':
        return accounts.find(a => a.code === '5000')?.id;
      case 'CASH':
        return accounts.find(a => a.code === '1000')?.id;
      case 'BANK':
        return accounts.find(a => a.code === '1100')?.id;
      default:
        return undefined;
    }
  };

  const columns: ColumnsType<Journal> = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      sorter: (a, b) => a.code.localeCompare(b.code),
      render: (code) => <Text strong>{code}</Text>
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name, record) => (
        <Space>
          {journalTypeIcons[record.type] || <BookOutlined />}
          <Text>{name}</Text>
        </Space>
      )
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      filters: [
        { text: 'Sales', value: 'SALE' },
        { text: 'Purchases', value: 'PURCHASE' },
        { text: 'Cash', value: 'CASH' },
        { text: 'Bank', value: 'BANK' },
        { text: 'General', value: 'GENERAL' }
      ],
      onFilter: (value, record) => record.type === value,
      render: (type: JournalType) => {
        const color = journalTypeColors[type] || 'default';
        const label = journalTypeLabels[type] || type;
        return (
          <Tag color={color}>
            {label}
          </Tag>
        );
      }
    },
    {
      title: 'Default Account',
      dataIndex: 'defaultAccountId',
      key: 'defaultAccountId',
      render: (accountId) => {
        if (!accountId) return <Text type="secondary">-</Text>;
        const account = accounts.find(a => a.id === accountId);
        return account ? (
          <Text>{account.code} - {account.name}</Text>
        ) : (
          <Text type="secondary">-</Text>
        );
      }
    },
    {
      title: 'Entries',
      dataIndex: ['_count', 'entries'],
      key: 'entries',
      width: 80,
      align: 'center',
      render: (count) => count || 0
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      width: 100,
      align: 'center',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false }
      ],
      onFilter: (value, record) => record.active === value,
      render: (active) => (
        <Tag
          color={active ? 'success' : 'default'}
          icon={active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {active ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title={record.active ? 'Deactivate' : 'Activate'}>
            <Button
              type="text"
              size="small"
              icon={record.active ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete Journal"
            description="Are you sure you want to delete this journal? This cannot be undone."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={(record._count?.entries || 0) > 0}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* Stats Cards */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Total Journals"
              value={stats?.total || 0}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Active"
              value={stats?.active || 0}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Statistic
              title="Inactive"
              value={stats?.inactive || 0}
              valueStyle={{ color: '#999' }}
              prefix={<CloseCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small">
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 12 }}>By Type</Text>
              <Space wrap size={4}>
                {stats?.byType?.map(item => (
                  <Tag key={item.type} color={journalTypeColors[item.type as JournalType]}>
                    {journalTypeLabels[item.type as JournalType]}: {item.count}
                  </Tag>
                ))}
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card
        title={
          <Space>
            <BookOutlined />
            <span>Journal Management</span>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              New Journal
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <div style={{ marginBottom: 16 }}>
          <Space wrap>
            <Search
              placeholder="Search by code or name..."
              style={{ width: 250 }}
              allowClear
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              prefix={<SearchOutlined />}
            />
            <Select
              placeholder="Filter by type"
              style={{ width: 150 }}
              value={filterType}
              onChange={(value) => setFilterType(value)}
            >
              <Option value="all">All Types</Option>
              <Option value="SALE">Sales</Option>
              <Option value="PURCHASE">Purchases</Option>
              <Option value="CASH">Cash</Option>
              <Option value="BANK">Bank</Option>
              <Option value="GENERAL">General</Option>
            </Select>
            <Select
              placeholder="Filter by status"
              style={{ width: 130 }}
              value={filterActive}
              onChange={(value) => setFilterActive(value)}
            >
              <Option value="all">All Status</Option>
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Space>
        </div>

        {/* Table */}
        <Table
          dataSource={filteredJournals}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} journals`
          }}
        />

        {/* Quick Setup Info */}
        {journals.length === 0 && !loading && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            background: '#fafafa',
            borderRadius: 8,
            marginTop: 16
          }}>
            <BookOutlined style={{ fontSize: 48, color: '#999', marginBottom: 16 }} />
            <Title level={4} type="secondary">No Journals Found</Title>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
              Journals are required for recording transactions. Create at least a Sales Journal (SAL) to post invoices.
            </Text>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                Create Your First Journal
              </Button>
            </Space>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingJournal ? 'Edit Journal' : 'Create Journal'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingJournal(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ active: true }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="code"
                label="Journal Code"
                rules={[
                  { required: true, message: 'Please enter journal code' },
                  { max: 10, message: 'Code must be at most 10 characters' },
                  { pattern: /^[A-Z0-9]+$/, message: 'Code must be uppercase letters/numbers only' }
                ]}
                tooltip="Short unique identifier (e.g., SAL, PUR, BNK)"
              >
                <Input
                  placeholder="e.g., SAL"
                  style={{ textTransform: 'uppercase' }}
                  onChange={(e) => form.setFieldValue('code', e.target.value.toUpperCase())}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="name"
                label="Journal Name"
                rules={[{ required: true, message: 'Please enter journal name' }]}
              >
                <Input placeholder="e.g., Sales Journal" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="type"
            label="Journal Type"
            rules={[{ required: true, message: 'Please enter or select journal type' }]}
            tooltip="Determines the purpose and default behavior of this journal. You can select a predefined type or enter a custom type."
            extra="Select from predefined types or type your own custom journal type"
          >
            <Select
              placeholder="Select or type custom journal type"
              showSearch
              mode="tags"
              maxTagCount={1}
              onChange={(value: any) => {
                // Get the first value if it's an array (tags mode returns array)
                const type = Array.isArray(value) ? value[0] : value;
                // Auto-suggest default account based on type
                const defaultAccountId = getDefaultAccountForType(type);
                if (defaultAccountId && !form.getFieldValue('defaultAccountId')) {
                  form.setFieldValue('defaultAccountId', defaultAccountId);
                }
              }}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children as any)?.toString().toLowerCase().includes(input.toLowerCase())
              }
            >
              {/* Predefined standard types */}
              <Option value="SALE">
                <Space>
                  <FileTextOutlined style={{ color: '#52c41a' }} />
                  <span>Sales Journal</span>
                </Space>
              </Option>
              <Option value="PURCHASE">
                <Space>
                  <ShoppingOutlined style={{ color: '#fa8c16' }} />
                  <span>Purchase Journal</span>
                </Space>
              </Option>
              <Option value="CASH">
                <Space>
                  <DollarOutlined style={{ color: '#1890ff' }} />
                  <span>Cash Journal</span>
                </Space>
              </Option>
              <Option value="BANK">
                <Space>
                  <BankOutlined style={{ color: '#722ed1' }} />
                  <span>Bank Journal</span>
                </Space>
              </Option>
              <Option value="GENERAL">
                <Space>
                  <BookOutlined />
                  <span>General Journal</span>
                </Space>
              </Option>
              {/* Custom types from existing journals */}
              {(() => {
                const standardTypes = ['SALE', 'PURCHASE', 'CASH', 'BANK', 'GENERAL'];
                const customTypes = [...new Set(journals.map(j => j.type))]
                  .filter(type => !standardTypes.includes(type));
                return customTypes.map(type => (
                  <Option key={type} value={type}>
                    <Space>
                      <BookOutlined />
                      <span>{type}</span>
                    </Space>
                  </Option>
                ));
              })()}
            </Select>
          </Form.Item>

          <Form.Item
            name="defaultAccountId"
            label="Default Account"
            tooltip="The default account to use when creating entries in this journal"
          >
            <Select
              allowClear
              placeholder="Select default account (optional)"
              showSearch
              // Search the label, not the children. Written as two expressions
              // either side of a dash, the children are the array
              // ['1100', ' - ', 'Cash'] rather than a string, and the old filter
              // called toLowerCase() straight on it — which threw on every
              // keystroke and took the whole page down with it.
              optionFilterProp="label"
            >
              {accounts.map(account => (
                <Option key={account.id} value={account.id} label={`${account.code} - ${account.name}`}>
                  {account.code} - {account.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {editingJournal && (
            <Form.Item
              name="active"
              label="Status"
              valuePropName="checked"
            >
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Form.Item>
          )}
        </Form>

        {/* Common Journals Quick Reference */}
        {!editingJournal && (
          <div style={{
            marginTop: 16,
            padding: 12,
            background: '#f6f8fa',
            borderRadius: 6,
            border: '1px solid #e8e8e8'
          }}>
            <Text strong style={{ fontSize: 12 }}>Common Journals:</Text>
            <div style={{ marginTop: 8 }}>
              <Space wrap size={[8, 4]}>
                <Tag color="green">SAL - Sales Journal</Tag>
                <Tag color="orange">PUR - Purchase Journal</Tag>
                <Tag color="blue">CSH - Cash Journal</Tag>
                <Tag color="purple">BNK - Bank Journal</Tag>
                <Tag>GEN - General Journal</Tag>
              </Space>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default JournalManagement;